import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  GlobalStyles,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import {
  DEFAULT_REFERRAL_EXPORT_FIELDS,
  describeExportFilters,
  formatExportTimestamp,
  REFERRAL_EXPORT_CONFIDENTIAL,
  REFERRAL_EXPORT_FIELD_LABELS,
  REFERRAL_EXPORT_FIELDS,
  REFERRAL_EXPORT_MAX_REFERRALS,
  REFERRAL_EXPORT_TITLE,
  ReferralExportFields,
  type ReferralExportField,
  type ReferralListFilters,
} from '@hv/shared';
import { downloadReferralCsv, useReferralExportReport } from '../api/hooks';
import { SpiritG } from '../components/Brand';
import { describeError, useNotify } from '../components/Notify';
import { BRAND } from '../theme';
import { referralFiltersFromParams } from './filters';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

interface DialogProps {
  open: boolean;
  onClose: () => void;
  filters: ReferralListFilters;
  filterParams: URLSearchParams;
  total: number;
}

export function ReferralExportDialog({ open, onClose, filters, filterParams, total }: DialogProps) {
  const { notify, notifyError } = useNotify();
  const [fields, setFields] = useState<ReadonlySet<ReferralExportField>>(new Set(DEFAULT_REFERRAL_EXPORT_FIELDS));
  const [downloading, setDownloading] = useState(false);
  const chosen = REFERRAL_EXPORT_FIELDS.filter((f) => fields.has(f));
  const tooLarge = total > REFERRAL_EXPORT_MAX_REFERRALS;
  const blocked = chosen.length === 0 || tooLarge;
  const reportParams = new URLSearchParams(filterParams);
  reportParams.set('fields', chosen.join(','));

  const toggle = (f: ReferralExportField, on: boolean) =>
    setFields((prev) => {
      const next = new Set(prev);
      if (on) next.add(f);
      else next.delete(f);
      return next;
    });

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      await downloadReferralCsv(filters, chosen);
      notify('success', 'CSV export downloaded');
      onClose();
    } catch (err) {
      notifyError(err, 'Could not export referrals');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="referral-export-title">
      <DialogTitle id="referral-export-title">Export referrals</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          Exports the {plural(total, 'referral')} matching the current filters, one row per child. Scores and HV data are never included. Every export is recorded in
          the audit log.
        </Typography>
        {tooLarge && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            An export holds at most {REFERRAL_EXPORT_MAX_REFERRALS} referrals. Narrow the filters first.
          </Alert>
        )}
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography variant="h4" component="h3">
            Fields
          </Typography>
          <Button size="small" onClick={() => setFields(new Set(DEFAULT_REFERRAL_EXPORT_FIELDS))}>
            Reset to defaults
          </Button>
        </Stack>
        <FormGroup role="group" aria-label="Fields to export" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {REFERRAL_EXPORT_FIELDS.map((f) => (
            <FormControlLabel key={f} control={<Checkbox size="small" checked={fields.has(f)} onChange={(e) => toggle(f, e.target.checked)} />} label={REFERRAL_EXPORT_FIELD_LABELS[f]} />
          ))}
        </FormGroup>
        {chosen.length === 0 && (
          <Typography variant="body2" color="error" role="alert" mt={1}>
            Pick at least one field.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          startIcon={<PrintIcon />}
          href={`/referrals/report?${reportParams}`}
          target="_blank"
          rel="noopener"
          disabled={blocked}
          onClick={onClose}
        >
          Printable report
        </Button>
        <Button variant="contained" startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />} disabled={blocked || downloading} onClick={downloadCsv}>
          Download CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const printStyles = (
  <GlobalStyles
    styles={{
      '@page': { size: 'letter landscape', margin: '0.5in' },
      '@media print': {
        body: { backgroundColor: '#fff' },
        '.no-print': { display: 'none !important' },
        '.report': { padding: '0 !important' },
        '.report table': { fontSize: '9.5px' },
        '.report tr': { breakInside: 'avoid' },
      },
      '.report header, .report thead th': { printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' },
    }}
  />
);

export function ReferralReportPage({ params }: { params: URLSearchParams }) {
  const parsedFields = ReferralExportFields.safeParse(params.get('fields') ?? undefined);
  const fields = parsedFields.success ? parsedFields.data : [...DEFAULT_REFERRAL_EXPORT_FIELDS];
  const report = useReferralExportReport(referralFiltersFromParams(params, params.get('cycle') ?? undefined), fields);

  if (report.isPending) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <CircularProgress aria-label="Loading report" />
      </Box>
    );
  }
  if (report.error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Could not build the report: {describeError(report.error)}</Alert>
      </Box>
    );
  }
  const r = report.data;
  const rowCount = `${plural(r.referralCount, 'referral')} · ${plural(r.rows.length, 'row')}`;
  return (
    <Box className="report" sx={{ p: 3, bgcolor: '#fff', minHeight: '100vh', color: BRAND.ink }}>
      {printStyles}
      <Stack className="no-print" direction="row" gap={1} mb={2} alignItems="center">
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print or save as PDF
        </Button>
        <Typography variant="body2" color="text.secondary">
          Landscape layout. Choose “Save as PDF” in the print dialog to keep a copy.
        </Typography>
      </Stack>
      <Stack component="header" direction="row" alignItems="center" gap={2} sx={{ bgcolor: BRAND.navy, color: '#fff', px: 2.5, py: 2, borderBottom: `4px solid ${BRAND.gold}` }}>
        <SpiritG size={44} color="#fff" />
        <Box>
          <Typography component="h1" sx={{ fontSize: 18, fontWeight: 700, m: 0 }}>
            {REFERRAL_EXPORT_TITLE}
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.5 }}>{REFERRAL_EXPORT_CONFIDENTIAL}</Typography>
        </Box>
      </Stack>
      <Box component="dl" aria-label="Report details" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.25, fontSize: 12, my: 1.5, '& dt': { fontWeight: 700, color: BRAND.navy }, '& dd': { m: 0 } }}>
        <dt>Generated at</dt>
        <dd>{formatExportTimestamp(r.generatedAt)}</dd>
        <dt>Generated by</dt>
        <dd>{r.generatedBy}</dd>
        <dt>Filters</dt>
        <dd>{describeExportFilters(r.filters)}</dd>
        <dt>Contents</dt>
        <dd>{rowCount}</dd>
      </Box>
      <Box
        component="table"
        aria-label="Referral report"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
          '& th': { bgcolor: BRAND.navy, color: '#fff', textAlign: 'left', px: 1, py: 0.75, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `3px solid ${BRAND.gold}` },
          '& td': { px: 1, py: 0.75, borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'top' },
          '& tbody tr:nth-of-type(even) td': { bgcolor: BRAND.bg },
        }}
      >
        <thead>
          <tr>
            {r.headers.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {r.rows.map((row, i) => (
            <tr key={i} data-testid="report-row">
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
          {r.rows.length === 0 && (
            <tr>
              <td colSpan={r.headers.length}>No referrals match these filters.</td>
            </tr>
          )}
        </tbody>
      </Box>
      <Typography component="footer" sx={{ mt: 1.5, pt: 1, borderTop: `2px solid ${BRAND.navy}`, fontSize: 11 }}>
        Confidential · {REFERRAL_EXPORT_TITLE} · {rowCount}
      </Typography>
    </Box>
  );
}
